import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { savePaymentMethod } from '../Actions/cartActions';
import CheckoutSteps from '../Components/CheckoutSteps'
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import './PaymentMethodScreen.css';
import paytmLogo from '../Media/paytm.png';
import phonepeLogo from '../Media/phonepe.svg';
import razorpayLogo from '../Media/razorpay.svg';
import { ORDER_CREATE_RESET } from '../Constants/orderConstants';

export default function PaymentMethodScreen(props) {
    const cart = useSelector(state => state.cart);
    const { shippingAddress, cartItems } = cart;

    if(!shippingAddress.state){
        props.history.push('/shipping');
    }

    // Determine the specific item coming from Delivery
    const checkoutSingle = useMemo(() => {
        try{ return JSON.parse(localStorage.getItem('checkoutItem') || 'null'); }catch(e){ return null; }
    }, []);
    const currentItem = useMemo(() => {
        const pid = checkoutSingle?.productId;
        if (!pid) return null;
        return (cartItems || []).find(ci => ci.product === pid) || null;
    }, [cartItems, checkoutSingle]);

    const [paymentMethod, setPaymentMethod] = useState('COD');
    const [methods, setMethods] = useState([]); // [{key,label,disabled}]
    const [loading, setLoading] = useState(true);

    const dispatch = useDispatch();

    const submitHandler = (e) =>{
        e.preventDefault();
        dispatch(savePaymentMethod(paymentMethod));
        props.history.push('/placeorder');
    };

    useEffect(() => {
        // Reset any previous order success/state when entering payment method
        try { dispatch({ type: ORDER_CREATE_RESET }); } catch (_) {}
        const load = async () => {
            try{
                setLoading(true);
                const ref = doc(firestore, 'COMPANY DETAILS', '63qcu0OcfuZT9kOiRuNw');
                const snap = await getDoc(ref);
                const data = snap.exists() ? (snap.data() || {}) : {};
                const pm = data.paymentMethod || data.paymentMethods || {};

                // Normalize entries to array of uppercase keys
                let keys = [];
                if (Array.isArray(pm)){
                    // pm is like: [ { paytm: true }, { phonepay: true }, { razerpay: true } ]
                    pm.forEach((obj) => {
                        if (obj && typeof obj === 'object'){
                            Object.entries(obj).forEach(([k,v]) => {
                                if (v === true) keys.push(String(k).toUpperCase());
                            });
                        }
                    });
                } else if (typeof pm === 'object' && pm){
                    Object.entries(pm).forEach(([k,v]) => { if (v === true) keys.push(String(k).toUpperCase()); });
                }
                // Uniq
                keys = Array.from(new Set(keys));

                // Pretty label map
                const labelFor = (k) => {
                    switch(k){
                        case 'PAYTM': return 'Paytm';
                        case 'PHONEPAY':
                        case 'PHONEPE': return 'PhonePe';
                        case 'RAZERPAY':
                        case 'RAZORPAY': return 'Razorpay';
                        case 'ONLINE': return 'Pay Online';
                        case 'COD': return 'Cash on Delivery';
                        default: return k.charAt(0) + k.slice(1).toLowerCase();
                    }
                };

                const list = [];
                // Always include COD option (disabled if product doesn't support COD)
                list.push({ key: 'COD', label: labelFor('COD'), disabled: currentItem ? currentItem.COD === false : false });
                // Add company gateways for the rest (ONLINE, PAYTM, PHONEPE, RAZORPAY, etc.)
                keys.forEach((k) => {
                    if (k === 'COD') return; // already added above
                    list.push({ key: k, label: labelFor(k), disabled: false });
                });
                setMethods(list);
                // Default selection: prefer COD if enabled else first enabled
                const firstEnabled = list.find(m => !m.disabled);
                if (firstEnabled){
                    setPaymentMethod(firstEnabled.key);
                }
            }finally{
                setLoading(false);
            }
        };
        load();
    }, [currentItem]);

    return (
        <div>
            <CheckoutSteps step1 step2 step3></CheckoutSteps>
            <form className="form" onSubmit={submitHandler}>
                <div>
                    <h1>Payment Method</h1>
                </div>
                {loading ? (
                    <div className="card card-body pm-card">
                        <div className="loading-container" style={{ minHeight: 120 }}>
                            <div className="loading-spinner"></div>
                            <div className="loading-text">Loading payment methods…</div>
                        </div>
                    </div>
                ) : methods.length === 0 ? (
                    <div className="card card-body pm-card">
                        <div className="loading-text">No payment methods available. Please try again later.</div>
                    </div>
                ) : (
                    <div className="card card-body pm-card">
                        <div className="pm-list">
                            {methods.map((m) => {
                                const selected = paymentMethod === m.key;
                                let logo = null;
                                if (m.key === 'PAYTM') logo = paytmLogo;
                                if (['PHONEPE','PHONEPAY'].includes(m.key)) logo = phonepeLogo;
                                if (['RAZORPAY','RAZERPAY'].includes(m.key)) logo = razorpayLogo;
                                return (
                                    <label
                                        key={m.key}
                                        htmlFor={`pm-${m.key}`}
                                        className={`pm-row ${selected ? 'selected' : ''} ${m.disabled ? 'disabled' : ''}`}
                                        onClick={(e) => {
                                            // Do not toggle when clicking the input directly; input will handle it
                                            if (m.disabled) return;
                                            try {
                                                // If the click originated outside the input, select this method
                                                const target = e.target;
                                                if (!(target instanceof HTMLInputElement)) {
                                                    setPaymentMethod(m.key);
                                                }
                                            } catch (_) {}
                                        }}
                                        onKeyDown={(e) => {
                                            if (m.disabled) return;
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setPaymentMethod(m.key);
                                            }
                                        }}
                                        tabIndex={m.disabled ? -1 : 0}
                                    >
                                        <input
                                            type="radio"
                                            id={`pm-${m.key}`}
                                            value={m.key}
                                            name="paymentMethod"
                                            required
                                            disabled={m.disabled}
                                            checked={selected}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                        />
                                        <div className="pm-row-logo">
                                            {logo ? (
                                                <img src={logo} alt={m.label} />
                                            ) : (
                                                <span className="pm-fallback">{m.key === 'ONLINE' ? 'OL' : (m.key === 'COD' ? 'COD' : 'PAY')}</span>
                                            )}
                                        </div>
                                        <div className="pm-row-info">
                                            <div className="pm-row-title">{m.label}</div>
                                            {m.key === 'COD' && m.disabled ? (
                                                <div className="pm-row-note">Not available for this product</div>
                                            ) : m.disabled ? (
                                                <div className="pm-row-note">Not available</div>
                                            ) : null}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}
                <div>
                    <button className="primary pm-continue" type="submit" disabled={methods.length === 0 || loading}>
                        Continue
                    </button>
                </div>
            </form>
        </div>
    )
}
